<script lang="ts">
import { StatusCodes } from 'http-status-codes';
import Vue from 'vue';
import { mapGetters } from 'vuex';
import structuredData from '@core/mixins/structuredData';
import { LOCALE_DEFAULT } from '@model/const/locales';
import { HttpRejection } from '@model/http/rejection';
import { getOgImage, getTextDirection, getHtmlLang } from '@utils/html-meta';
import CareersJobComponent from './component.vue';

export default Vue.extend({
    name: 'CareersJobContainer',

    mixins: [structuredData],

    async serverPrefetch(): Promise<void> {
        // Init
        const { locale, id } = this.$route.params;

        if (!id) {
            const fixedURL = `/${locale}/careers/jobs/`;
            const rejection = new HttpRejection('Premature rendering stop', StatusCodes.MOVED_TEMPORARILY);
            rejection.addHeader('Location', fixedURL);
            throw rejection;
        }

        await this.$store.dispatch('workday/getJobById', id);

        const job = this.$store.state.workday?.items?.[0];

        if (!job) {
            const fixedURL = `/${locale}/careers/jobs/#error=404&id=${id}`;
            const rejection = new HttpRejection('Premature rendering stop', StatusCodes.MOVED_TEMPORARILY);
            rejection.addHeader('Location', fixedURL);
            throw rejection;
        }

        // TODO: should we throw 404 on department or slug mismatch?

        // Get linked entities
        const promises: Promise<void>[] = [
            this.$store.dispatch('slices/getSyncedData', { slice: 'global-meta', locale: LOCALE_DEFAULT }),
            this.$store.dispatch('slices/getSyncedData', { slice: 's-main-header', locale }),
            this.$store.dispatch('slices/getSyncedData', { slice: 's-global-footer', locale }),
            this.$store.dispatch('slices/getSyncedData', { slice: 's-job-description', locale }),
            this.$store.dispatch('seo/getSyncedData', { key: 'routes-config', locale }),
            this.$store.dispatch('seo/getSyncedData', { key: 'schema-website', locale }),
            this.$store.dispatch('seo/getSyncedData', { key: 'schema-webpage', locale }),
            this.$store.dispatch('seo/getSyncedData', { key: 'schema-organization', locale }),
            this.$store.dispatch('seo/getSyncedData', { key: 'schema-contact-point', locale }),
            this.$store.dispatch('seo/getSyncedData', { key: 'schema-offer', locale }),
            this.$store.dispatch('seo/getSyncedData', { key: 'schema-postal-address', locale }),
            this.$store.dispatch('seo/getSyncedData', { key: 'schema-video-object', locale }),
            this.$store.dispatch('seo/getSyncedData', { key: 'schema-faq-page', locale }),
            this.$store.dispatch('seo/getSyncedData', { key: 'schema-breadcrumbs', locale }),
            this.$store.dispatch('seo/getSyncedData', { key: 'schema-job-posting', locale }),
            this.$store.dispatch('seo/getSyncedData', { key: 'schema-product', locale }),
        ];

        await Promise.all(promises);

        // Building page meta
        this.setPocket(job);
        this.$ssrContext.res.meta = await this.getMeta();
    },

    computed: {
        ...mapGetters('config', ['$config']),
    },

    methods: {
        setPocket(job) {
            this.$ssrContext.res.pocket.ga_job_params = {
                pageDetailsA: job.categories?.[0]?.descriptor ?? '(not set)', // Job Department
                pageDetailsB: job.title, // Job Title
                pageDetailsC: '(not set)', // Potential future usage
            };
            this.$ssrContext.res.pocket.ga_dl_params = {
                DL_Audience: 'Generic',
                DL_PageType: 'Company',
            };
        },

        async getMeta(): Promise<any> {
            const { locale } = this.$route.params;

            const ensureTrailingSlash = (input: string) => input.replace(/\/*?$/, '/');

            const self = ensureTrailingSlash(`https://${this.$config.domain}${this.$route.path}/`);

            const meta: any = this.$store.state.slices.items['s-job-description'].pageMeta || {};
            const canonical = meta.canonical ? ensureTrailingSlash(meta.canonical) : self;
            const globalMeta = this.$store.state.slices.items['global-meta'] || {};
            const imgSrc = meta.image || `@${globalMeta.defaultOgImage}`;
            const ogimage = getOgImage(imgSrc, this.$config.env.HEAD_SITE_MAIN_PUBLIC_BASE_URL_STORAGE);
            const title = meta.title || 'Acronis';
            const description = meta.description || title;

            const ldJsonSchema = await this.getStructuredDataMarkup({
                siteID: this.$config.siteID,
                domain: this.$config.domain,
                schemasConfig: this.getStaticConfigs,
                routesConfig: this.getRoutesConfig,
            });

            return {
                title,
                head: [
                    { tag: 'meta', name: 'title', content: title },
                    { tag: 'meta', name: 'description', content: description },
                    { tag: 'meta', property: 'og:title', content: title },
                    { tag: 'meta', property: 'og:description', content: meta['og:description'] || description },
                    { tag: 'meta', property: 'og:image', content: ogimage },
                    { tag: 'meta', property: 'og:url', content: self },
                    { tag: 'meta', name: 'twitter:title', content: title },
                    {
                        tag: 'meta',
                        name: 'twitter:description',
                        content: meta['twitter:description'] || description,
                    },
                    { tag: 'meta', name: 'twitter:image', content: ogimage },
                    { tag: 'meta', name: 'twitter:url', content: self },
                    { tag: 'link', rel: 'image_src', href: ogimage },
                    { tag: 'link', rel: 'canonical', href: canonical },
                ],
                htmlAttrs: {
                    dir: getTextDirection(locale),
                    lang: getHtmlLang(locale),
                },
                ldJsonSchema,
            };
        },
    },

    render(h) {
        return h(CareersJobComponent);
    },
});
</script>
